import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Mail, Lock, Loader2 } from 'lucide-react'
import { useAuth } from '../store/auth'

export function Login() {
  const navigate = useNavigate()
  const login = useAuth((s) => s.login)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const result = await login(email, password)
      if (result.ok) {
        navigate({ to: '/' })
      } else if (result.status === 429) {
        setError('Demasiados intentos. Esperá unos minutos.')
      } else if (result.status === 403) {
        setError(result.error || 'No tenés permiso para acceder al panel')
      } else {
        setError(result.error || 'Credenciales inválidas')
      }
    } catch {
      setError('Error de servidor, intentá más tarde')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="grid min-h-screen w-screen grid-cols-1 lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2">
            <div className="grid size-10 place-items-center rounded-lg bg-brand-600 font-extrabold text-white">
              M
            </div>
            <div className="leading-tight">
              <div className="text-lg font-extrabold text-ink-900">MAZ Admin</div>
              <div className="text-[10px] uppercase tracking-wider text-ink-500">
                Panel administrativo
              </div>
            </div>
          </div>

          <h1 className="mb-2 text-3xl font-extrabold tracking-tight text-ink-900">
            Bienvenido de vuelta
          </h1>
          <p className="mb-8 text-sm text-ink-500">
            Ingresá tus credenciales para acceder al panel administrativo.
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink-700">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-ink-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none transition-colors focus:border-brand-500"
                />
              </div>
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="text-xs font-semibold text-ink-700">Contraseña</label>
                <a href="#" className="text-xs font-medium text-brand-600 hover:text-brand-700">
                  ¿Olvidaste?
                </a>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl border border-ink-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none transition-colors focus:border-brand-500"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {loading && <Loader2 className="size-4 animate-spin" />}
              {loading ? 'Validando…' : 'Iniciar sesión'}
            </button>
          </form>

          <div className="mt-8 text-center text-xs text-ink-500">
            Acceso restringido. Solo administradores y operadores autorizados.
          </div>
        </div>
      </div>

      {/* Right: brand panel */}
      <div className="relative hidden overflow-hidden bg-gradient-to-br from-ink-900 via-ink-900 to-brand-900 lg:block">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 30% 50%, rgba(239, 68, 68, 0.5) 0%, transparent 50%)',
          }}
        />
        <div className="relative h-full" />
      </div>
    </div>
  )
}
